import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, countOf, tweensAxis } from '../skillUsage';

export const driftingMeshGroundSkill: CodeoSkill = {
    name: SKILL_NAMES.driftingMeshGround,
    kind: 'codeo',
    description:
        'A vivid brand-gradient ground made of four soft colour blobs over a base colour, drifting ' +
        'fast enough to be seen within a one-second beat. Cheap to render (no blur filter), and ' +
        'the ground a calm typographic video cuts to for its brightest beats.',
    checkLooksFor: 'several radial-gradient blobs over a base colour, each tweened on x or y',
    documentUsesSkill: check(
        (script, markup) =>
            countOf(markup, /radial-gradient\(/g) >= 2 &&
            (tweensAxis(script, 'x') || tweensAxis(script, 'y')),
    ),
    instructions: `# Drifting Mesh Ground

The ground is a gradient that is alive: a base colour with **four large, soft colour blobs** over it, each drifting on its own vector. Where they overlap the colours mix, so the ground reads as one continuous brand gradient (pink into violet into blue) that is visibly flowing. The drift is fast, hundreds of pixels a second, because a ground holds for only about a second before the next cut and a slow drift would never be seen.

Each blob is a plain \`radial-gradient\` that fades from its colour to transparent. No \`filter: blur\`, so the ground costs almost nothing to render and can sit under several beats.

## Markup

The blobs are larger than the frame and centred near the frame's corners and edges, so the colour reaches every edge. Their fade is soft all the way out: the colour at 0%, three-quarters strength at 40%, a tenth at 80%, nothing at the rim.

\`\`\`html
<div id="mesh" data-l5-absolute data-l5-allow-offscreen style="position: absolute; inset: 0; overflow: hidden; background: var(--l5-color-2, #825be5);">
  <div class="blob" data-l5-absolute style="position: absolute; left: -740px; top: -1088px; width: 1820px; height: 2236px; background: radial-gradient(closest-side, var(--l5-color-3, #f1649c) 0%, color-mix(in srgb, var(--l5-color-3, #f1649c) 71%, transparent) 40%, color-mix(in srgb, var(--l5-color-3, #f1649c) 41%, transparent) 60%, color-mix(in srgb, var(--l5-color-3, #f1649c) 13%, transparent) 80%, transparent 100%);"></div>
  <div class="blob" data-l5-absolute style="position: absolute; left: -681px; top: 321px; width: 1656px; height: 1318px; background: radial-gradient(closest-side, var(--l5-color-4, #465ef4) 0%, color-mix(in srgb, var(--l5-color-4, #465ef4) 71%, transparent) 40%, color-mix(in srgb, var(--l5-color-4, #465ef4) 41%, transparent) 60%, color-mix(in srgb, var(--l5-color-4, #465ef4) 13%, transparent) 80%, transparent 100%);"></div>
  <div class="blob" data-l5-absolute style="position: absolute; left: 826px; top: -57px; width: 1922px; height: 2358px; background: radial-gradient(closest-side, var(--l5-color-5, #9d5dfc) 0%, color-mix(in srgb, var(--l5-color-5, #9d5dfc) 71%, transparent) 40%, color-mix(in srgb, var(--l5-color-5, #9d5dfc) 41%, transparent) 60%, color-mix(in srgb, var(--l5-color-5, #9d5dfc) 13%, transparent) 80%, transparent 100%);"></div>
  <div class="blob" data-l5-absolute style="position: absolute; left: 1051px; top: -592px; width: 1396px; height: 1370px; background: radial-gradient(closest-side, var(--l5-color-6, #5e51fa) 0%, color-mix(in srgb, var(--l5-color-6, #5e51fa) 71%, transparent) 40%, color-mix(in srgb, var(--l5-color-6, #5e51fa) 41%, transparent) 60%, color-mix(in srgb, var(--l5-color-6, #5e51fa) 13%, transparent) 80%, transparent 100%);"></div>
</div>
\`\`\`

## Timeline code

Every blob drifts in a straight line at a constant speed for the whole time the ground is on screen. The vectors are fixed constants, different for each blob.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 1.2;
// px per second: the pink sweeps right across the top, the purple sweeps left along the bottom.
const DRIFT = [
  { x: 395, y: -70 },
  { x: -31, y: -190 },
  { x: -409, y: 85 },
  { x: 62, y: 242 },
];
gsap.utils.toArray('#mesh .blob').forEach((blob, i) => {
  tl.fromTo(blob, { x: 0, y: 0 }, { x: DRIFT[i].x * SCENE, y: DRIFT[i].y * SCENE, duration: SCENE, ease: 'none', immediateRender: false }, 0);
});
tl.set({}, {}, SCENE);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Palette.** The base and blobs take palette slots, so the ground follows the video's colours: base a mid violet, then the pink end, a blue, a lighter violet and a deep indigo. A pastel version (lavender base, blush, lilac and periwinkle blobs) makes a light ground that takes ink-coloured type.
- **Across several beats.** When the mesh returns later in the graphic, continue each blob from where it would be by then (offset its start by the drift times the elapsed time), so it reads as the same living ground.
- **Gradient-filled type.** The same blobs can paint a word: put the mesh layer inside a span with \`background-clip: text\` and transparent text, or use the key-word variant of \`${SKILL_NAMES.textureMaskedType}\`.
- **Speed.** 300–400px/s for the fastest blob on a one-second beat; halve it on a ground that holds two seconds.

## Pitfalls

- **No blur filter.** The softness is in the gradient stops. A \`filter: blur\` on a blob this size is the most expensive thing a frame can render.
- **Ease none, whole hold.** A drift that eases out stops, and a stopped mesh is a flat gradient.
- **White type only on the saturated mesh.** Check the type against the lightest patch the drift brings under it.
- Blobs are transform-tweened, so their position lives in \`left\`/\`top\`, not in a CSS \`transform\`.`,
};
